import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar as CalendarIcon,
  CaretLeft,
  CaretRight,
  Plus,
  TrashSimple,
  Pencil,
  X,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  fetchAppointments,
  createAppointment,
  updateAppointment,
  deleteAppointment,
  fetchTrucks,
} from "@/lib/api";
import { useAutoRefresh } from "@/lib/useAutoRefresh";

const TYPE_LABEL = {
  expert: "Expert",
  clim: "Recharge clim",
  controle_technique: "Contrôle technique",
  revision: "Révision",
  mecanique: "Mécanique",
  carrosserie: "Carrosserie",
  autre: "Autre",
};

const TYPE_COLOR = {
  expert: "#7C3AED",
  clim: "#0EA5E9",
  controle_technique: "#F59E0B",
  revision: "#10B981",
  mecanique: "#EF4444",
  carrosserie: "#EC4899",
  autre: "#52525B",
};

const STATUS_LABEL = {
  planifie: "Planifié",
  fait: "Fait",
  annule: "Annulé",
};

const FR_MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
const FR_DAYS_SHORT = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function pad(n) { return n < 10 ? "0" + n : "" + n; }
function fmtDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function parseISO(s) {
  if (!s) return null;
  return new Date(s);
}
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

/**
 * Searchable truck picker — type the license plate (or brand) to filter.
 * Falls back to a scrollable dropdown showing all trucks when the input is empty.
 */
function TruckSearchPicker({ trucks, value, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const selected = useMemo(
    () => trucks.find((t) => t.id === value) || null,
    [trucks, value]
  );

  // Keep the visible text in sync when a truck is selected externally
  useEffect(() => {
    if (selected) setQuery(`${selected.license_plate} · ${selected.brand || ""}`.trim());
    else setQuery("");
  }, [selected]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    // If the query exactly matches the selected truck's display, show all
    const selectedDisplay = selected
      ? `${selected.license_plate} · ${selected.brand || ""}`.trim().toLowerCase()
      : "";
    if (!q || q === selectedDisplay) return trucks;
    return trucks.filter((t) => {
      const plate = (t.license_plate || "").toLowerCase();
      const brand = (t.brand || "").toLowerCase();
      const site = (t.site || "").toLowerCase();
      return plate.includes(q) || brand.includes(q) || site.includes(q);
    });
  }, [trucks, query, selected]);

  const pick = (t) => {
    onChange(t.id);
    setQuery(`${t.license_plate} · ${t.brand || ""}`.trim());
    setOpen(false);
  };

  const clear = () => {
    onChange("");
    setQuery("");
    setOpen(true);
  };

  return (
    <div ref={containerRef} className="relative" data-testid="input-rdv-truck">
      <div className="relative">
        <MagnifyingGlass
          size={14}
          weight="bold"
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none"
        />
        <Input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // if user starts typing again after a selection, unselect
            if (value) onChange("");
          }}
          onFocus={() => setOpen(true)}
          placeholder="Rechercher une plaque (ex: HM-768)..."
          className="rounded-none border-zinc-900 pl-8 pr-8"
          autoComplete="off"
          data-testid="input-rdv-truck-search"
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
            aria-label="Effacer"
            data-testid="input-rdv-truck-clear"
          >
            <X size={14} weight="bold" />
          </button>
        )}
      </div>
      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-zinc-900 shadow-lg"
          data-testid="input-rdv-truck-list"
        >
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-zinc-500 text-center">
              Aucun camion ne correspond à « {query} »
            </div>
          )}
          {filtered.slice(0, 100).map((t) => {
            const isActive = t.id === value;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => pick(t)}
                className={`w-full text-left px-3 py-1.5 text-xs border-b border-zinc-100 last:border-b-0 flex items-center gap-2 ${
                  isActive ? "bg-zinc-900 text-white" : "hover:bg-zinc-100"
                }`}
                data-testid={`input-rdv-truck-opt-${t.id}`}
              >
                <span className="font-bold uppercase mono">{t.license_plate}</span>
                <span className={isActive ? "opacity-80" : "text-zinc-500"}>
                  · {t.brand || "—"}
                </span>
                {t.site && (
                  <span
                    className={`ml-auto px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest ${
                      isActive ? "bg-white text-zinc-900" : "bg-zinc-900 text-white"
                    }`}
                  >
                    {t.site}
                  </span>
                )}
              </button>
            );
          })}
          {filtered.length > 100 && (
            <div className="px-3 py-2 text-[10px] text-zinc-500 text-center border-t border-zinc-200">
              …et {filtered.length - 100} autre(s). Affinez votre recherche.
            </div>
          )}
        </div>
      )}
    </div>
  );
}



export default function CalendarPage() {
  const navigate = useNavigate();
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [appts, setAppts] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [dayDialogDate, setDayDialogDate] = useState(null);
  const [siteFilter, setSiteFilter] = useState("all"); // "all" | "DLP4" | "DWB9"
  const [form, setForm] = useState({
    truck_id: "",
    date: "",
    time: "10:00",
    type: "autre",
    title: "",
    location: "",
    notes: "",
    status: "planifie",
  });

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [a, t] = await Promise.all([fetchAppointments(), fetchTrucks()]);
      setAppts(a);
      setTrucks(t);
    } catch {
      if (!silent) toast.error("Erreur de chargement");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(() => load({ silent: true }), 20000);

  const monthDays = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    // Monday-first
    const firstWeekday = (first.getDay() + 6) % 7;
    const days = [];
    for (let i = 0; i < firstWeekday; i++) {
      days.push({ inMonth: false, date: new Date(first.getFullYear(), first.getMonth(), -firstWeekday + i + 1) });
    }
    for (let d = 1; d <= last.getDate(); d++) {
      days.push({ inMonth: true, date: new Date(first.getFullYear(), first.getMonth(), d) });
    }
    while (days.length % 7 !== 0) {
      const lastDate = days[days.length - 1].date;
      const next = new Date(lastDate);
      next.setDate(next.getDate() + 1);
      days.push({ inMonth: false, date: next });
    }
    return days;
  }, [cursor]);

  const truckById = useMemo(() => {
    const m = {};
    for (const t of trucks) m[t.id] = t;
    return m;
  }, [trucks]);

  // Filter appointments by site (based on truck's site). Also compute per-site counts.
  const filteredAppts = useMemo(() => {
    if (siteFilter === "all") return appts;
    return appts.filter((a) => {
      const truck = truckById[a.truck_id];
      return truck && truck.site === siteFilter;
    });
  }, [appts, siteFilter, truckById]);

  const siteCounts = useMemo(() => {
    const counts = { all: appts.length, DLP4: 0, DWB9: 0 };
    for (const a of appts) {
      const truck = truckById[a.truck_id];
      if (truck?.site === "DLP4") counts.DLP4 += 1;
      else if (truck?.site === "DWB9") counts.DWB9 += 1;
    }
    return counts;
  }, [appts, truckById]);

  const apptsByDay = useMemo(() => {
    const map = {};
    for (const a of filteredAppts) {
      const d = parseISO(a.date);
      if (!d) continue;
      const key = fmtDate(d);
      (map[key] = map[key] || []).push(a);
    }
    return map;
  }, [filteredAppts]);

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => setCursor(new Date(today.getFullYear(), today.getMonth(), 1));

  const openNew = (date = null) => {
    setEditing(null);
    const d = date || new Date();
    setForm({
      truck_id: trucks[0]?.id || "",
      date: fmtDate(d),
      time: "10:00",
      type: "autre",
      title: "",
      location: "",
      notes: "",
      status: "planifie",
    });
    setDialogOpen(true);
  };

  const openEdit = (appt) => {
    setEditing(appt);
    const d = parseISO(appt.date);
    setForm({
      truck_id: appt.truck_id,
      date: d ? fmtDate(d) : "",
      time: d ? `${pad(d.getHours())}:${pad(d.getMinutes())}` : "10:00",
      type: appt.type,
      title: appt.title || "",
      location: appt.location || "",
      notes: appt.notes || "",
      status: appt.status,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.truck_id) return toast.error("Sélectionner un camion");
    if (!form.date) return toast.error("Sélectionner une date");
    const iso = new Date(`${form.date}T${form.time}:00`).toISOString();
    const body = {
      truck_id: form.truck_id,
      date: iso,
      type: form.type,
      title: form.title,
      location: form.location,
      notes: form.notes,
    };
    try {
      if (editing) {
        await updateAppointment(editing.id, { ...body, status: form.status });
        toast.success("RDV mis à jour");
      } else {
        await createAppointment(body);
        toast.success("RDV créé");
      }
      setDialogOpen(false);
      load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const remove = async (appt) => {
    if (!window.confirm("Supprimer ce RDV ?")) return;
    try {
      await deleteAppointment(appt.id);
      load();
      toast.success("Supprimé");
    } catch { toast.error("Erreur"); }
  };

  const upcoming = useMemo(() => {
    // Show ONLY today's appointments
    const now = new Date();
    return filteredAppts
      .filter((a) => {
        const d = parseISO(a.date);
        return d && sameDay(d, now);
      })
      .sort((a, b) => parseISO(a.date) - parseISO(b.date));
  }, [filteredAppts]);

  const validateAppt = async (appt) => {
    try {
      await updateAppointment(appt.id, { status: "fait" });
      toast.success("RDV validé · ajouté à l'historique du camion");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const cancelAppt = async (appt) => {
    if (!window.confirm("Annuler ce RDV ?")) return;
    try {
      await updateAppointment(appt.id, { status: "annule" });
      toast.success("RDV annulé");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <div className="label-tag">NY Trans</div>
          <h1 className="serif-display text-4xl md:text-5xl mt-1">
            <CalendarIcon size={28} weight="bold" className="inline mr-2 -mt-1 text-[var(--ny-gold)]" />
            Calendrier
          </h1>
          <div className="ny-divider w-32 mt-2" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={goToday} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-white hover:bg-zinc-900 hover:text-white" data-testid="cal-today">
            Aujourd&apos;hui
          </button>
          <div className="flex items-center gap-1">
            <button onClick={goPrev} className="p-2 border border-zinc-900 bg-white hover:bg-zinc-900 hover:text-white" data-testid="cal-prev"><CaretLeft size={16} weight="bold" /></button>
            <div className="px-4 py-2 border border-zinc-900 font-bold uppercase tracking-tight min-w-[180px] text-center" data-testid="cal-month-label">
              {FR_MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
            </div>
            <button onClick={goNext} className="p-2 border border-zinc-900 bg-white hover:bg-zinc-900 hover:text-white" data-testid="cal-next"><CaretRight size={16} weight="bold" /></button>
          </div>
          <button onClick={() => openNew()} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-2" data-testid="cal-new">
            <Plus size={14} weight="bold" /> Nouveau RDV
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        {/* Calendar grid */}
        <div className="lg:col-span-3 border border-zinc-900 bg-white">
          {/* Site filter tabs */}
          <div className="flex items-stretch border-b border-zinc-900" data-testid="cal-site-filter">
            {[
              { key: "all", label: "TOUS", count: siteCounts.all, color: "bg-zinc-900" },
              { key: "DLP4", label: "DLP4 · PERPIGNAN", count: siteCounts.DLP4, color: "bg-[var(--ny-gold)]" },
              { key: "DWB9", label: "DWB9 · TOULOUSE", count: siteCounts.DWB9, color: "bg-[var(--ny-gold-deep)]" },
            ].map((tab) => {
              const active = siteFilter === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setSiteFilter(tab.key)}
                  className={`flex-1 px-3 py-2.5 text-[11px] font-bold uppercase tracking-widest border-r border-zinc-900 last:border-r-0 transition-colors ${
                    active
                      ? `${tab.color} ${tab.key === "DLP4" ? "text-zinc-900" : "text-white"}`
                      : "bg-white text-zinc-700 hover:bg-zinc-100"
                  }`}
                  data-testid={`cal-site-tab-${tab.key}`}
                >
                  {tab.label}
                  <span className={`ml-2 mono ${active ? "opacity-90" : "text-zinc-400"}`}>
                    · {tab.count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-7 border-b border-zinc-900">
            {FR_DAYS_SHORT.map((d) => (
              <div key={d} className="px-2 py-2 text-xs font-bold uppercase tracking-widest text-zinc-500 text-center border-r last:border-r-0 border-zinc-200">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {monthDays.map((cell, i) => {
              const key = fmtDate(cell.date);
              const dayAppts = apptsByDay[key] || [];
              const isToday = sameDay(cell.date, today);
              return (
                <div
                  key={key}
                  className={`min-h-[88px] sm:min-h-[110px] p-1 border-r border-b border-zinc-200 last-of-type:border-r-0 ${
                    cell.inMonth ? "bg-white" : "bg-zinc-50"
                  } ${isToday ? "ring-2 ring-inset ring-zinc-900" : ""} cursor-pointer hover:bg-zinc-50`}
                  onClick={() => { setDayDialogDate(cell.date); setDayDialogOpen(true); }}
                  data-testid={`cal-day-${key}`}
                >
                  <div className={`text-[10px] font-bold ${cell.inMonth ? "text-zinc-900" : "text-zinc-400"} ${isToday ? "bg-zinc-900 text-white px-1 inline-block" : ""}`}>
                    {cell.date.getDate()}
                  </div>
                  <div className="mt-0.5 space-y-px">
                    {dayAppts.slice(0, 5).map((a) => {
                      const truck = truckById[a.truck_id];
                      return (
                        <div
                          key={a.id}
                          onClick={(e) => { e.stopPropagation(); openEdit(a); }}
                          className="text-[8px] sm:text-[9px] leading-tight px-1 py-0.5 truncate font-bold uppercase text-white"
                          style={{ background: TYPE_COLOR[a.type] || "#52525B", textDecoration: a.status === "annule" ? "line-through" : "none", opacity: a.status === "fait" ? 0.45 : 1 }}
                          title={`${truck?.license_plate || "—"} · ${TYPE_LABEL[a.type]}${a.title ? ` · ${a.title}` : ""}`}
                          data-testid={`cal-appt-${a.id}`}
                        >
                          {truck?.license_plate || "—"} · {TYPE_LABEL[a.type]}
                        </div>
                      );
                    })}
                    {dayAppts.length > 5 && (
                      <div className="text-[9px] text-zinc-500 font-bold">+{dayAppts.length - 5}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Today's appointments */}
        <aside className="border border-zinc-900 bg-white p-3 lg:col-span-1" data-testid="cal-today-list">
          <div className="label-tag mb-2">RDV du jour</div>
          {upcoming.length === 0 && (
            <div className="text-sm text-zinc-500 py-4 text-center">Aucun RDV aujourd&apos;hui</div>
          )}
          {upcoming.map((a) => {
            const d = parseISO(a.date);
            const truck = truckById[a.truck_id];
            const done = a.status === "fait";
            const cancelled = a.status === "annule";
            return (
              <div
                key={a.id}
                className="border border-zinc-200 hover:border-zinc-900 p-2 mb-2"
                data-testid={`today-appt-${a.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span
                    className="px-1.5 py-0.5 text-[10px] font-bold uppercase text-white"
                    style={{ background: TYPE_COLOR[a.type], opacity: done || cancelled ? 0.5 : 1 }}
                  >
                    {TYPE_LABEL[a.type]}
                  </span>
                  <span className="text-xs font-mono">
                    {pad(d?.getHours())}:{pad(d?.getMinutes())}
                  </span>
                </div>
                <div className="mt-1 text-sm font-bold uppercase" style={{ textDecoration: cancelled ? "line-through" : "none" }}>
                  {truck?.license_plate || "—"}
                </div>
                {a.title && <div className="text-xs text-zinc-600">{a.title}</div>}
                {done && <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mt-1">✓ Effectué</div>}
                {cancelled && <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-1">Annulé</div>}
                <div className="flex flex-wrap gap-1 mt-2">
                  {!done && !cancelled && (
                    <>
                      <button
                        onClick={() => validateAppt(a)}
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid={`validate-appt-${a.id}`}
                      >
                        ✓ Valider
                      </button>
                      <button
                        onClick={() => cancelAppt(a)}
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-red-600 hover:text-red-600"
                        data-testid={`cancel-appt-${a.id}`}
                      >
                        ✕ Annuler
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => openEdit(a)}
                    className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900"
                  >
                    Modifier
                  </button>
                  {truck && (
                    <button
                      onClick={() => navigate(`/trucks/${truck.id}`)}
                      className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900"
                    >
                      Voir →
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </aside>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="cal-legend">
        {Object.keys(TYPE_LABEL).map((k) => (
          <div key={k} className="inline-flex items-center gap-1.5 text-xs">
            <span className="w-3 h-3 inline-block" style={{ background: TYPE_COLOR[k] }} />
            <span className="font-bold uppercase tracking-widest">{TYPE_LABEL[k]}</span>
          </div>
        ))}
      </div>

      {/* RDV dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-md w-[calc(100vw-1.5rem)] sm:w-auto max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
              <CalendarIcon size={22} weight="bold" />
              {editing ? "Modifier RDV" : "Nouveau RDV"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-tag">Camion *</Label>
              <TruckSearchPicker
                trucks={trucks}
                value={form.truck_id}
                onChange={(id) => setForm({ ...form, truck_id: id })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="label-tag">Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none border-zinc-900" data-testid="input-rdv-date" />
              </div>
              <div>
                <Label className="label-tag">Heure</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-none border-zinc-900" data-testid="input-rdv-time" />
              </div>
            </div>
            <div>
              <Label className="label-tag">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="rounded-none border-zinc-900" data-testid="input-rdv-type"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  {Object.entries(TYPE_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Titre (optionnel)</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Garage Renault Perpignan" className="rounded-none border-zinc-900" data-testid="input-rdv-title" />
            </div>
            <div>
              <Label className="label-tag">Lieu (optionnel)</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Adresse / garage" className="rounded-none border-zinc-900" />
            </div>
            <div>
              <Label className="label-tag">Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none border-zinc-900" rows={3} />
            </div>
            {editing && (
              <div>
                <Label className="label-tag">Statut</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-none border-zinc-900"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {Object.entries(STATUS_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            {editing && (
              <button onClick={() => { remove(editing); setDialogOpen(false); }} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-red-600 text-red-600 hover:bg-red-600 hover:text-white inline-flex items-center gap-2 mr-auto" data-testid="cal-delete">
                <TrashSimple size={14} weight="bold" /> Supprimer
              </button>
            )}
            <button onClick={() => setDialogOpen(false)} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900 inline-flex items-center gap-2">
              <X size={14} weight="bold" /> Annuler
            </button>
            <button onClick={save} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-2" data-testid="cal-save">
              <Pencil size={14} weight="bold" /> {editing ? "Mettre à jour" : "Créer"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Day list dialog */}
      <Dialog open={dayDialogOpen} onOpenChange={setDayDialogOpen}>
        <DialogContent className="rounded-none border border-zinc-900 max-w-lg w-[calc(100vw-1.5rem)] sm:w-auto max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl sm:text-2xl font-black uppercase tracking-tight inline-flex items-center gap-2" style={{ fontFamily: "Chivo" }}>
              <CalendarIcon size={22} weight="bold" />
              {dayDialogDate?.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {(() => {
              if (!dayDialogDate) return null;
              const list = apptsByDay[fmtDate(dayDialogDate)] || [];
              if (list.length === 0) {
                return (
                  <div className="py-6 text-center text-sm text-zinc-500 border border-dashed border-zinc-300">
                    Aucun RDV ce jour
                  </div>
                );
              }
              return list
                .slice()
                .sort((a, b) => parseISO(a.date) - parseISO(b.date))
                .map((a) => {
                  const d = parseISO(a.date);
                  const truck = truckById[a.truck_id];
                  const done = a.status === "fait";
                  const cancelled = a.status === "annule";
                  return (
                    <div key={a.id} className="border border-zinc-200 p-2" data-testid={`daylist-appt-${a.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" style={{ background: TYPE_COLOR[a.type], opacity: done || cancelled ? 0.5 : 1 }}>
                          {TYPE_LABEL[a.type]}
                        </span>
                        <span className="text-xs font-mono">{pad(d?.getHours())}:{pad(d?.getMinutes())}</span>
                      </div>
                      <div className="mt-1 text-sm font-bold uppercase" style={{ textDecoration: cancelled ? "line-through" : "none" }}>
                        {truck?.license_plate || "—"} <span className="font-normal text-xs text-zinc-500">{truck?.brand}</span>
                      </div>
                      {a.title && <div className="text-xs text-zinc-700">{a.title}</div>}
                      {a.location && <div className="text-xs text-zinc-500">{a.location}</div>}
                      {done && <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 mt-1">✓ Effectué</div>}
                      {cancelled && <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mt-1">Annulé</div>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {!done && !cancelled && (
                          <>
                            <button
                              onClick={async () => { await validateAppt(a); setDayDialogOpen(false); }}
                              className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest bg-emerald-600 text-white hover:bg-emerald-700"
                              data-testid={`daylist-validate-${a.id}`}
                            >
                              ✓ Valider
                            </button>
                            <button
                              onClick={async () => { await cancelAppt(a); setDayDialogOpen(false); }}
                              className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-red-600 hover:text-red-600"
                              data-testid={`daylist-cancel-${a.id}`}
                            >
                              ✕ Annuler
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => { setDayDialogOpen(false); openEdit(a); }}
                          className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900"
                        >
                          Modifier
                        </button>
                        {truck && (
                          <button
                            onClick={() => navigate(`/trucks/${truck.id}`)}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-zinc-300 hover:border-zinc-900"
                          >
                            Voir fiche →
                          </button>
                        )}
                      </div>
                    </div>
                  );
                });
            })()}
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setDayDialogOpen(false)} className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-300 hover:border-zinc-900">
              Fermer
            </button>
            <button
              onClick={() => { setDayDialogOpen(false); openNew(dayDialogDate); }}
              className="px-3 py-2 text-xs uppercase tracking-widest font-bold border border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-700 inline-flex items-center gap-2"
              data-testid="daylist-new"
            >
              <Plus size={14} weight="bold" /> Ajouter un RDV ce jour
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
